import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { isServer } from './isServer'
import { getPageContent } from './api'
import { serverData } from './serverData'

// Market Insights index.
// The server injects the published-article list into the #insightsdata
// island so crawlers/AI see it in the initial HTML. On client-side
// navigation the component falls back to fetching the `insights`
// contents row from the API and filtering to published articles.

const metaTitle = 'Market Insights | Kellands Real Estate'
const metaDescription =
  "Auckland property market commentary, updates and buyer/seller guides from the team at Kellands Real Estate."

const months = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

export const formatArticleDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

const readIsland = () => {
  if (isServer) return null
  const islandEl = document.getElementById('insightsdata')
  if (islandEl && islandEl.innerHTML.trim()) {
    try {
      const parsed = JSON.parse(islandEl.innerHTML)
      // Index island holds the published list; ignore a stale single-article
      // object left in the island by client-side nav from an article page.
      if (Array.isArray(parsed)) return parsed
    } catch (e) {
      // fall through to API fetch
    }
  }
  return null
}

const InsightsIndex = () => {
  // Server: read the list the route stashed (renders the list into the HTML).
  // Client: read the data island, else fetch on client-side nav.
  const [articles, setArticles] = useState(() =>
    isServer ? (Array.isArray(serverData.insightList) ? serverData.insightList : null) : readIsland()
  )

  // Client-side nav fallback: fetch the contents row, filter to published
  useEffect(() => {
    if (articles === null) {
      getPageContent('insights', (data) => {
        if (data && data.length && data[0].value) {
          try {
            setArticles(JSON.parse(data[0].value).filter(a => a.published))
          } catch (e) {
            setArticles([])
          }
        } else {
          setArticles([])
        }
      }, () => setArticles([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const list = articles || []

  return (
    <div className="main-body">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.kellands.co.nz/insights" />
      </Helmet>

      <div className="column suburb-page">
        <h1 className="main-page-header">Market insights</h1>
        <p className="suburb-headline">
          Auckland property market commentary, updates and guides from the Kellands team.
        </p>

        {list.length === 0 ? (
          <p>New market insights are on the way — check back soon.</p>
        ) : (
          <div className="insight-index-list">
            {list.map(a => (
              <article className="insight-index-item" key={a.slug}>
                <h2>
                  <Link to={`/insights/${a.slug}`}>{a.title}</Link>
                </h2>
                {a.date && (
                  <p className="insight-meta">{formatArticleDate(a.date)}</p>
                )}
                {a.excerpt && <p className="insight-excerpt">{a.excerpt}</p>}
                <Link className="insight-readmore" to={`/insights/${a.slug}`}>
                  Read more
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default InsightsIndex
